import React, { useEffect, useRef } from "react";

/** Scopes — luma waveform + RGB histogram, computed from the preview pixels
 *  (spec §2.3; GPU compute lands with the WebGPU port). */
export const Scopes = ({ pixels }) => {
  const waveRef = useRef(null);
  const histRef = useRef(null);

  useEffect(() => {
    if (!pixels) return;
    const { data, width, height } = pixels;
    const W = 256, H = 128;

    // ── Waveform (luma) ──
    const wctx = waveRef.current?.getContext("2d");
    if (wctx) {
      wctx.fillStyle = "#0c0a09";
      wctx.fillRect(0, 0, W, H);
      const img = wctx.createImageData(W, H);
      const step = Math.max(1, Math.floor((width * height) / 60000));
      for (let p = 0; p < width * height; p += step) {
        const i = p * 4;
        const x = Math.floor(((p % width) / width) * W);
        const luma = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        const y = H - 1 - Math.floor((luma / 255) * (H - 1));
        const di = (y * W + x) * 4;
        img.data[di] = 120; img.data[di + 1] = 220; img.data[di + 2] = 120;
        img.data[di + 3] = Math.min(255, img.data[di + 3] + 90);
      }
      wctx.putImageData(img, 0, 0);
    }

    // ── RGB histogram ──
    const hctx = histRef.current?.getContext("2d");
    if (hctx) {
      const bins = [new Array(256).fill(0), new Array(256).fill(0), new Array(256).fill(0)];
      const step = Math.max(4, Math.floor((width * height) / 80000) * 4);
      for (let i = 0; i < data.length; i += step) {
        bins[0][data[i]]++; bins[1][data[i + 1]]++; bins[2][data[i + 2]]++;
      }
      const peak = Math.max(...bins.map((b) => Math.max(...b))) || 1;
      hctx.fillStyle = "#0c0a09";
      hctx.fillRect(0, 0, W, H);
      hctx.globalCompositeOperation = "screen";
      ["#f87171", "#4ade80", "#60a5fa"].forEach((color, c) => {
        hctx.strokeStyle = color;
        hctx.beginPath();
        for (let x = 0; x < 256; x++) {
          const y = H - 1 - (bins[c][x] / peak) * (H - 4);
          x === 0 ? hctx.moveTo(x, y) : hctx.lineTo(x, y);
        }
        hctx.stroke();
      });
      hctx.globalCompositeOperation = "source-over";
    }
  }, [pixels]);

  return (
    <div className="grid grid-cols-2 gap-2" data-testid="grade-scopes">
      <div>
        <p className="text-[9px] font-mono uppercase tracking-widest text-white/30 mb-1">Waveform</p>
        <canvas ref={waveRef} width={256} height={128} className="w-full rounded-md border border-white/10" />
      </div>
      <div>
        <p className="text-[9px] font-mono uppercase tracking-widest text-white/30 mb-1">Histogram</p>
        <canvas ref={histRef} width={256} height={128} className="w-full rounded-md border border-white/10" />
      </div>
    </div>
  );
};
